import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr } from "@/lib/format";
import { ImageIcon, UploadSimpleIcon, SpinnerIcon, XCircleIcon } from "@phosphor-icons/react";

/**
 * <ImageInput /> — Input híbrido: permite pegar una URL o subir un archivo a R2.
 * Cuando se sube un archivo, llama POST /api/uploads y reemplaza el value con la URL pública.
 *
 * Props:
 *  - value: string (URL actual)
 *  - onChange: (url: string) => void
 *  - folder: string (default "landing") — carpeta lógica en el bucket
 *  - placeholder: string
 *  - preview: boolean (default true) — muestra miniatura si hay value
 *
 * Si R2 no está configurado en el backend, el botón de subir mostrará un tooltip explicativo.
 */
export default function ImageInput({
  value,
  onChange,
  folder = "landing",
  placeholder = "https://… o sube una imagen",
  preview = true,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imágenes (jpg, png, webp, gif, svg)");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Imagen muy grande (máximo 10 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", folder);
      fd.append("kind", "image");
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data?.url) {
        toast.error("Subida OK pero el servidor no devolvió URL pública. Revisa R2_PUBLIC_BASE_URL.");
        return;
      }
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        {preview && (
          <div
            className="flex-shrink-0 w-12 h-12 rounded-md bg-app border border-border overflow-hidden flex items-center justify-center"
            data-testid="image-input-preview"
          >
            {value ? (
              <img src={value} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <ImageIcon size={20} className="text-text-muted" />
            )}
          </div>
        )}
        <div className="relative flex-1">
          <ImageIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full bg-app border border-border rounded-md pl-8 pr-9 py-2 text-sm focus:border-agro-green outline-none"
            data-testid="image-input-url"
          />
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-red-400"
              title="Limpiar" aria-label="Limpiar"
              data-testid="image-input-clear"
            >
              <XCircleIcon size={16} />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-border rounded-md hover:border-agro-green/40 hover:bg-white/5 disabled:opacity-50 whitespace-nowrap"
          data-testid="image-input-upload-btn"
        >
          {uploading ? (
            <>
              <SpinnerIcon size={14} className="animate-spin" /> Subiendo…
            </>
          ) : (
            <>
              <UploadSimpleIcon size={14} weight="bold" /> Subir
            </>
          )}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
          data-testid="image-input-file"
        />
      </div>
    </div>
  );
}
